import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, XCircle, AlertCircle, Play, Trash2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { buildVapiEvent, sendWebhookEvent, checkWebhookConnectivity } from '@/lib/buildVapiEvent';
import { Link } from 'react-router-dom';

interface TestResult {
  name: string;
  status: 'PASS' | 'FAIL' | 'PENDING';
  details?: string;
  expected?: any;
  actual?: any;
}

interface TestScenario {
  name: string;
  events: any[];
  assertions: string[];
}

const QATestHarness = () => {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [testData, setTestData] = useState<any>({});
  const [creditsTestResults, setCreditsTestResults] = useState<any>(null);
  const [isRunningCreditsTest, setIsRunningCreditsTest] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsMessage, setSmsMessage] = useState('This is a test SMS from your AI calling system. If you receive this, SMS delivery is working correctly!');
  const [isSendingSms, setIsSendingSms] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Configuration
  const TEST_CONFIG = {
    WEBHOOK_URL: import.meta.env.VITE_VAPI_WEBHOOK_URL || 'https://giedkdxtrqcqzcockacd.supabase.co/functions/v1/vapi-webhook',
    TEST_USER: user?.id || '',
    APP_URL: import.meta.env.VITE_APP_URL || 'https://app.callsparker.com'
  };

  const buildTestScenarios = (campaignId: string) => [
    {
      name: "Scenario 1: Consent + Notify (no prospect email)",
      events: [
        buildVapiEvent("log_consent", 
          { phrase: "Yes, call me next week", slot: "Tue morning" },
          {
            callId: "test_call_abc",
            userId: TEST_CONFIG.TEST_USER,
            campaignId,
            prospectId: "test_pros_123",
            prospectLabel: "Acme Plumbing"
          }
        ),
        buildVapiEvent("notify_owner",
          {
            type: "consent_callback",
            slot: "Tue morning",
            summary: "Consent captured; prefers early next week."
          },
          {
            callId: "test_call_abc",
            userId: TEST_CONFIG.TEST_USER,
            campaignId,
            prospectId: "test_pros_123",
            prospectLabel: "Acme Plumbing"
          }
        )
      ],
      assertions: [
        "call_actions has 2 rows for this prospect",
        "owner email queued with correct subject/body"
      ]
    },
    {
      name: "Scenario 2: Safe Mode v2 - Schedule Confirmed",
      events: [
        buildVapiEvent("schedule_confirmed",
          {
            phrase: "Great — you're set for Wednesday at 2.",
            day: "Wednesday",
            time: "2",
            scheduled_time: "2025-08-16T14:00:00-04:00"
          },
          {
            callId: "test_call_schedule",
            userId: TEST_CONFIG.TEST_USER,
            campaignId,
            prospectId: "test_pros_schedule",
            prospectLabel: "Schedule Test Co"
          }
        )
      ],
      assertions: [
        "Canonical line: 'Great — you're set for Wednesday at 2.'",
        "schedule_confirmed tool called with day=Wednesday, time=2",
        "call_actions has 1 new row with type='schedule_confirmed'",
        "action_data.type = 'schedule' with slot_confirmed = true",
        "Action appears in Action Center"
      ]
    },
    {
      name: "Scenario 3: Safe Mode v2 - Email and Follow-up Consent",
      events: [
        buildVapiEvent("email_and_followup_consent",
          {
            phrase: "Thanks — I'll email you now, and I have your okay to follow up by phone.",
            email_requested: true
          },
          {
            callId: "test_call_email_consent",
            userId: TEST_CONFIG.TEST_USER,
            campaignId,
            prospectId: "test_pros_email_consent",
            prospectLabel: "Email Consent Test Co"
          }
        )
      ],
      assertions: [
        "Canonical line: 'Thanks — I'll email you now, and I have your okay to follow up by phone.'",
        "email_and_followup_consent tool called",
        "call_actions has 1 new row with type='email_and_followup_consent'",
        "action_data.type = 'email_followup_consent' with email_sent=true, consent=true",
        "Action appears in Action Center"
      ]
    },
    {
      name: "Scenario 4: Safe Mode v2 - Do Not Call",
      events: [
        buildVapiEvent("do_not_call",
          {
            phrase: "Understood — I won't call this number again.",
            dnc_request: true
          },
          {
            callId: "test_call_dnc",
            userId: TEST_CONFIG.TEST_USER,
            campaignId,
            prospectId: "test_pros_dnc",
            prospectLabel: "DNC Test Co"
          }
        )
      ],
      assertions: [
        "Canonical line: 'Understood — I won't call this number again.'",
        "do_not_call tool called",
        "call_actions has 1 new row with type='do_not_call'",
        "action_data.type = 'dnc' with consent=false",
        "Action appears in Action Center"
      ]
    },
    {
      name: "Scenario 5: Safe Mode v2 - General Callback Consent",
      events: [
        buildVapiEvent("consent_callback_general",
          {
            phrase: "Got it. I will call you back later."
          },
          {
            callId: "test_call_general_callback",
            userId: TEST_CONFIG.TEST_USER,
            campaignId,
            prospectId: "test_pros_general_callback",
            prospectLabel: "General Callback Test Co"
          }
        )
      ],
      assertions: [
        "Canonical line: 'Got it. I will call you back later.'",
        "consent_callback_general tool called",
        "call_actions has 1 new row with type='consent_callback_general'",
        "action_data.type = 'callback_consent' with window=false, consent=true",
        "Action appears in Action Center"
      ]
    },
    {
      name: "Scenario 4: Safe Mode - Appointment Booked",
      events: [
        buildVapiEvent("log_appointment_confirmed",
          {
            when_iso: "2025-08-12T15:30:00-04:00",
            human_readable: "Great—You're set for Tue at 3:30 PM.",
            timezone: "America/New_York",
            source: "safe_mode",
            script_stage: "confirm",
            context: { 
              campaign_id: campaignId, 
              prospect_id: "test_pros_safe_1", 
              operator: "safe_mode" 
            },
            notes: "QA scenario"
          },
          {
            callId: "test_call_safe_1",
            userId: TEST_CONFIG.TEST_USER,
            campaignId,
            prospectId: "test_pros_safe_1",
            prospectLabel: "Safe Mode Test Co"
          }
        )
      ],
      assertions: [
        "call_actions created with type='appointment_booked'",
        "action_data.type = 'appointment_confirmed'",
        "action_data.when_iso matches payload",
        "action_data.source = 'safe_mode'"
      ]
    },
    {
      name: "Scenario 5: Safe Mode - Reschedule → Final Confirm",
      events: [
        // Only send the final confirmed appointment
        buildVapiEvent("log_appointment_confirmed",
          {
            when_iso: "2025-08-13T10:00:00-04:00",
            human_readable: "Great—You're set for Wed at 10:00 AM.",
            timezone: "America/New_York",
            source: "safe_mode",
            script_stage: "confirm",
            context: { 
              campaign_id: campaignId, 
              prospect_id: "test_pros_safe_2", 
              operator: "safe_mode" 
            },
            notes: "Final time after reschedule"
          },
          {
            callId: "test_call_safe_2",
            userId: TEST_CONFIG.TEST_USER,
            campaignId,
            prospectId: "test_pros_safe_2",
            prospectLabel: "Reschedule Test Co"
          }
        )
      ],
      assertions: [
        "exactly one appointment_confirmed event logged",
        "final time stored correctly"
      ]
    },
    {
      name: "Scenario 6: Safe Mode - Declined / No Booking",
      events: [
        // No log_appointment_confirmed events - user declined all attempts
      ],
      assertions: [
        "zero appointment_confirmed events logged"
      ]
    },
    {
      name: "Scenario 7: Safe Mode - Email on Objection",
      events: [
        buildVapiEvent("log_consent",
          {
            channel: "phone",
            scope: "callback",
            text: "I'll send you the details and follow up later.",
            timestamp_iso: new Date().toISOString(),
            source: "safe_mode"
          },
          {
            callId: "test_call_safe_4",
            userId: TEST_CONFIG.TEST_USER,
            campaignId,
            prospectId: "test_pros_safe_4",
            prospectLabel: "Email Objection Test Co"
          }
        ),
        buildVapiEvent("create_follow_up_call",
          {
            due_at_iso: "2025-08-15T10:00:00-04:00",
            reason: "email_on_objection",
            notes: "Asked for info; follow up after send",
            source: "safe_mode"
          },
          {
            callId: "test_call_safe_4",
            userId: TEST_CONFIG.TEST_USER,
            campaignId,
            prospectId: "test_pros_safe_4",
            prospectLabel: "Email Objection Test Co"
          }
        )
      ],
      assertions: [
        "2 actions created: 1 consent + 1 follow_up_call",
        "action_data.type = 'follow_up_call'",
        "reason = 'email_on_objection'"
      ]
    },
    {
      name: "Scenario 8: Safe Mode - Fallback Derailed",
      events: [
        buildVapiEvent("log_consent",
          {
            channel: "phone",
            scope: "callback", 
            text: "I'll give you a quick call back later.",
            timestamp_iso: new Date().toISOString(),
            source: "safe_mode"
          },
          {
            callId: "test_call_safe_5",
            userId: TEST_CONFIG.TEST_USER,
            campaignId,
            prospectId: "test_pros_safe_5",
            prospectLabel: "Fallback Test Co"
          }
        ),
        buildVapiEvent("create_follow_up_call",
          {
            due_at_iso: "2025-08-16T14:00:00-04:00",
            reason: "fallback_derailed",
            notes: "Multiple curveballs; follow up needed",
            source: "safe_mode"
          },
          {
            callId: "test_call_safe_5",
            userId: TEST_CONFIG.TEST_USER,
            campaignId,
            prospectId: "test_pros_safe_5",
            prospectLabel: "Fallback Test Co"
          }
        )
      ],
      assertions: [
        "2 actions created: 1 consent + 1 follow_up_call",
        "action_data.type = 'follow_up_call'", 
        "reason = 'fallback_derailed'"
      ]
    }
  ];

  const setupTestData = async (): Promise<{ campaign: any; script: any; user: string } | null> => {
    try {
      addTestResult("Setup: Creating test data", 'PENDING');
      
      // Create test script first
      const { data: script, error: scriptError } = await supabase
        .from('scripts')
        .insert({
          user_id: TEST_CONFIG.TEST_USER,
          name: 'QA Test Script',
          content: 'Test script for QA automation'
        })
        .select()
        .single();

      if (scriptError) {
        updateTestResult("Setup: Creating test data", 'FAIL', `Script creation failed: ${scriptError.message}`);
        return null;
      }

      // Create test campaign with correct script_id
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .insert({
          user_id: TEST_CONFIG.TEST_USER,
          name: 'QA Test Campaign',
          description: 'Automated QA test campaign',
          script_id: script.id,
          status: 'active',
          campaign_type: 'outbound'
        })
        .select()
        .single();

      if (campaignError) {
        updateTestResult("Setup: Creating test data", 'FAIL', `Campaign creation failed: ${campaignError.message}`);
        return null;
      }

      const data = { campaign, script, user: TEST_CONFIG.TEST_USER };
      setTestData(data); // Update UI state
      updateTestResult("Setup: Creating test data", 'PASS', `Campaign ${campaign.id} created with script ${script.id}`);
      return data;
    } catch (error: any) {
      updateTestResult("Setup: Creating test data", 'FAIL', error.message);
      return null;
    }
  };

  const sendEvent = async (event: any): Promise<void> => {
    try {
      console.log(`Sending webhook: event_id=${event.event_id}, call_id=${event.call.id}, toolName=${event.data.toolName}`);
      
      const response = await fetch(TEST_CONFIG.WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      });

      console.log(`Webhook response: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        const truncatedError = errorText.length > 300 ? errorText.substring(0, 300) + '...' : errorText;
        throw new Error(`Webhook failed: ${response.status} ${response.statusText} - ${truncatedError}`);
      }
    } catch (error: any) {
      toast({
        title: "Webhook Failed",
        description: error.message,
        variant: "destructive"
      });
      throw error;
    }
  };

  const runAssertions = async (scenarios: any[], campaignId: string): Promise<void> => {
    // Assert: call_actions rows created (expect 5 total: 3 original + 2 safe mode)
    addTestResult("Assert: call_actions created correctly", 'PENDING');
    const { data: callActions, error: callActionsError } = await supabase
      .from('call_actions')
      .select('*')
      .eq('user_id', TEST_CONFIG.TEST_USER)
      .eq('campaign_id', campaignId);

    if (callActionsError) {
      updateTestResult("Assert: call_actions created correctly", 'FAIL', callActionsError.message);
    } else if (callActions && callActions.length === 5) {
      updateTestResult("Assert: call_actions created correctly", 'PASS', `Expected 5 actions created, got ${callActions.length}`);
    } else {
      updateTestResult("Assert: call_actions created correctly", 'FAIL', `Expected 5 actions, got ${callActions?.length || 0}`);
    }

    // Assert: email_queue rows created
    addTestResult("Assert: email_queue created correctly", 'PENDING');
    const { data: emailQueue, error: emailError } = await supabase
      .from('email_queue')
      .select('*')
      .eq('user_id', TEST_CONFIG.TEST_USER)
      .eq('campaign_id', campaignId);

    if (emailError) {
      updateTestResult("Assert: email_queue created correctly", 'FAIL', emailError.message);
    } else if (emailQueue && emailQueue.length === 1) {
      updateTestResult("Assert: email_queue created correctly", 'PASS', `Exactly 1 owner email queued`);
      addTestResult("Note: notify_owner validated via email_queue, not as Action Center item", 'PASS');
      
      // Check email template format
      const emailBody = emailQueue[0]?.body || '';
      const hasCorrectTemplate = emailBody.includes('Action Type:') && 
                                emailBody.includes('Prospect:') &&
                                emailBody.includes('Call ID:');
      
      addTestResult("Assert: email template format", hasCorrectTemplate ? 'PASS' : 'FAIL', 
        hasCorrectTemplate ? 'Template format correct' : 'Template format incorrect');
    } else {
      updateTestResult("Assert: email_queue created correctly", 'FAIL', `Expected exactly 1 email, got ${emailQueue?.length || 0}`);
    }

    // Test idempotency - run duplicate event and check no new rows created
    addTestResult("Assert: idempotency prevents duplicates", 'PENDING');
    const duplicateScenario = scenarios.find(s => s.name.includes('Idempotency'));
    if (duplicateScenario) {
      const initialCount = callActions?.length || 0;
      
      // Send same event again
      for (const event of duplicateScenario.events) {
        await sendEvent(event);
      }
      
      // Wait and check count
      await new Promise(resolve => setTimeout(resolve, 1000));
      const { data: afterDuplicateActions } = await supabase
        .from('call_actions')
        .select('*')
        .eq('user_id', TEST_CONFIG.TEST_USER)
        .eq('campaign_id', campaignId);
      
      const finalCount = afterDuplicateActions?.length || 0;
      
      if (finalCount === initialCount) {
        updateTestResult("Assert: idempotency prevents duplicates", 'PASS', `No duplicate rows created`);
      } else {
        updateTestResult("Assert: idempotency prevents duplicates", 'FAIL', `Expected same count, got ${finalCount} vs ${initialCount}`);
      }
    }

    // Assert: Actions page functionality
    addTestResult("Assert: Actions page loads correctly", 'PENDING');
    try {
      const { data: actionsPageData, error: actionsError } = await supabase
        .from('call_actions')
        .select('*,campaigns(name)')
        .eq('user_id', TEST_CONFIG.TEST_USER)
        .eq('processed', false);

      if (actionsError) {
        updateTestResult("Assert: Actions page loads correctly", 'FAIL', actionsError.message);
      } else {
        updateTestResult("Assert: Actions page loads correctly", 'PASS', `${actionsPageData?.length || 0} unprocessed actions found`);
      }
    } catch (error: any) {
      updateTestResult("Assert: Actions page loads correctly", 'FAIL', error.message);
    }

    // Assert: portal branding propagation to clients
    addTestResult("Assert: portal branding cascades to inherited clients", 'PENDING');
    try {
      // fetch the business portal row for this user
      const { data: portal, error: portalError } = await supabase
        .from('clients')
        .select('branding')
        .eq('user_id', TEST_CONFIG.TEST_USER)
        .eq('settings->>is_business_portal', 'true')
        .maybeSingle();

      if (portalError) {
        updateTestResult("Assert: portal branding cascades to inherited clients", 'FAIL', portalError.message);
      } else if (!portal) {
        updateTestResult("Assert: portal branding cascades to inherited clients", 'FAIL', 'No business portal found');
      } else {
        const { data: inherited, error: inhError } = await supabase
          .from('clients')
          .select('id,branding')
          .eq('user_id', TEST_CONFIG.TEST_USER)
          .eq('settings->>inherits_business_branding', 'true');

        if (inhError) {
          updateTestResult("Assert: portal branding cascades to inherited clients", 'FAIL', inhError.message);
        } else {
          const mismatches = (inherited || []).filter(c =>
            JSON.stringify(c.branding) !== JSON.stringify(portal.branding),
          );
          if (mismatches.length === 0) {
            updateTestResult("Assert: portal branding cascades to inherited clients", 'PASS', `${inherited?.length || 0} clients match portal branding`);
          } else {
            updateTestResult(
              "Assert: portal branding cascades to inherited clients",
              'FAIL',
              `${mismatches.length} client(s) had mismatched branding`,
            );
          }
        }
      }
    } catch (err: any) {
      updateTestResult("Assert: portal branding cascades to inherited clients", 'FAIL', err.message);
    }

    // Assert: Safe Mode appointment confirmations
    addTestResult("Assert: Safe Mode appointment confirmations", 'PENDING');
    const { data: appointmentActions, error: appointmentError } = await supabase
      .from('call_actions')
      .select('*')
      .eq('user_id', TEST_CONFIG.TEST_USER)
      .eq('campaign_id', campaignId)
      .eq('type', 'appointment_booked');

    if (appointmentError) {
      updateTestResult("Assert: Safe Mode appointment confirmations", 'FAIL', appointmentError.message);
    } else {
      // Should have exactly 2 appointment_booked actions (scenarios 4 and 5)
      const expectedAppointments = 2;
      if (appointmentActions && appointmentActions.length === expectedAppointments) {
        // Check that action_data has correct structure
        const validStructure = appointmentActions.every(action => {
          const actionData = action.action_data as any;
          return actionData?.type === 'appointment_confirmed' &&
                 actionData?.when_iso &&
                 actionData?.human_readable &&
                 actionData?.source === 'safe_mode';
        });
        
        if (validStructure) {
          updateTestResult("Assert: Safe Mode appointment confirmations", 'PASS', 
            `${expectedAppointments} appointment confirmations with correct structure`);
        } else {
          updateTestResult("Assert: Safe Mode appointment confirmations", 'FAIL', 
            'Appointment confirmations have incorrect action_data structure');
        }
      } else {
        updateTestResult("Assert: Safe Mode appointment confirmations", 'FAIL', 
          `Expected ${expectedAppointments} appointment confirmations, got ${appointmentActions?.length || 0}`);
      }
    }

    // Assert: Scenario 6 (declined) has no appointment events
    addTestResult("Assert: Declined scenario has no appointments", 'PENDING');
    const { data: declinedActions } = await supabase
      .from('call_actions')
      .select('*')
      .eq('user_id', TEST_CONFIG.TEST_USER)
      .eq('campaign_id', campaignId)
      .eq('prospect_id', 'test_pros_safe_declined');
      
    if (declinedActions && declinedActions.length === 0) {
      updateTestResult("Assert: Declined scenario has no appointments", 'PASS', 'No events for declined prospect');
    } else {
      updateTestResult("Assert: Declined scenario has no appointments", 'FAIL', 
        `Expected 0 events for declined prospect, got ${declinedActions?.length || 0}`);
    }

    // Assert: RLS prevents reading other users' data
    addTestResult("Assert: RLS prevents reading other users' data", 'PENDING');
    const { data: otherUserActions } = await supabase
      .from('call_actions')
      .select('*')
      .neq('user_id', TEST_CONFIG.TEST_USER);

    updateTestResult("Assert: RLS prevents reading other users' data", 
      otherUserActions?.length === 0 ? 'PASS' : 'FAIL',
      otherUserActions?.length === 0 ? 'RLS working correctly' : `Can see ${otherUserActions?.length} other user actions`);
  };

  const runFullTest = async () => {
    setIsRunning(true);
    setTestResults([]);

    try {
      // Connectivity check first
      addTestResult("Connectivity: Webhook ping test", 'PENDING');
      try {
        await checkWebhookConnectivity(TEST_CONFIG.WEBHOOK_URL);
        updateTestResult("Connectivity: Webhook ping test", 'PASS', 'Webhook is reachable');
      } catch (error: any) {
        updateTestResult("Connectivity: Webhook ping test", 'FAIL', error.message);
        setIsRunning(false);
        return;
      }

      // Setup
      const data = await setupTestData();
      if (!data) {
        setIsRunning(false);
        return;
      }

      const scenarios = buildTestScenarios(data.campaign.id);

      // Run scenarios
      for (const scenario of scenarios) {
        addTestResult(`Running: ${scenario.name}`, 'PENDING');
        
        try {
          for (const event of scenario.events) {
            await sendEvent(event);
          }
          updateTestResult(`Running: ${scenario.name}`, 'PASS', 'All events sent successfully');
        } catch (error: any) {
          updateTestResult(`Running: ${scenario.name}`, 'FAIL', `FAIL at step: webhook POST — ${error.message}`);
          setIsRunning(false);
          return;
        }
      }

      // Poll for call_actions with timeout (250ms polling for 3s)
      addTestResult("Polling: call_actions creation", 'PENDING');
      const pollStart = Date.now();
      const pollTimeout = 3000; // 3 seconds
      let actionsFound = false;
      
      while (Date.now() - pollStart < pollTimeout && !actionsFound) {
        const { data: actions } = await supabase
          .from('call_actions')
          .select('id')
          .eq('user_id', TEST_CONFIG.TEST_USER)
          .eq('campaign_id', data.campaign.id);
        
        if (actions && actions.length > 0) {
          actionsFound = true;
          updateTestResult("Polling: call_actions creation", 'PASS', `${actions.length} actions found`);
        } else {
          await new Promise(resolve => setTimeout(resolve, 250));
        }
      }
      
      if (!actionsFound) {
        updateTestResult("Polling: call_actions creation", 'FAIL', 'No call_actions created within expected time');
        setIsRunning(false);
        return;
      }

      // Run assertions
      await runAssertions(scenarios, data.campaign.id);

      // Add summary
      const actionCount = testResults.find(r => r.name.includes('call_actions'))?.status === 'PASS' ? 5 : 0;
      const emailCount = testResults.find(r => r.name.includes('email_queue'))?.status === 'PASS' ? 1 : 0;
      const idempotentPass = testResults.find(r => r.name.includes('idempotency'))?.status === 'PASS';
      const safeModePass = testResults.find(r => r.name.includes('Safe Mode appointment'))?.status === 'PASS';
      
      addTestResult("Summary", 'PASS', `Actions: ${actionCount} | Owner emails: ${emailCount} | Idempotent: ${idempotentPass ? 'PASS' : 'FAIL'} | Safe Mode: ${safeModePass ? 'PASS' : 'FAIL'}`);

      toast({
        title: "QA Test Complete",
        description: `Actions: ${actionCount} | Owner emails: ${emailCount} | Safe Mode: ${safeModePass ? 'PASS' : 'FAIL'}`
      });

    } catch (error: any) {
      const errorMsg = `FAIL at step: ${error.message}`;
      console.error(errorMsg);
      toast({
        title: "Test Failed",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setIsRunning(false);
    }
  };

  const cleanupTestData = async () => {
    try {
      addTestResult("Cleanup: Removing test data", 'PENDING');

      // Delete test data in correct order (actions → campaigns → scripts)
      const deleteResults = await Promise.allSettled([
        supabase.from('call_actions').delete().eq('user_id', TEST_CONFIG.TEST_USER).like('call_id', 'test_call_%'),
        supabase.from('email_queue').delete().eq('user_id', TEST_CONFIG.TEST_USER).like('call_id', 'test_call_%')
      ]);

      await Promise.allSettled([
        supabase.from('campaigns').delete().eq('user_id', TEST_CONFIG.TEST_USER).eq('name', 'QA Test Campaign')
      ]);

      await Promise.allSettled([
        supabase.from('scripts').delete().eq('user_id', TEST_CONFIG.TEST_USER).eq('name', 'QA Test Script')
      ]);

      updateTestResult("Cleanup: Removing test data", 'PASS', 'Test data cleaned up');
      setTestData({});
      
      toast({
        title: "Cleanup Complete",
        description: "All test data has been removed"
      });
    } catch (error: any) {
      updateTestResult("Cleanup: Removing test data", 'FAIL', error.message);
      toast({
        title: "Cleanup Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const runCreditsE2ETest = async () => {
    setIsRunningCreditsTest(true);
    setCreditsTestResults(null);

    try {
      toast({
        title: "Starting Credits E2E Test",
        description: "Running comprehensive credit system test...",
      });

      const response = await supabase.functions.invoke('credits-e2e-test', {
        body: {}
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      setCreditsTestResults(response.data);

      if (response.data.status === 'PASS') {
        toast({
          title: "Credits E2E Test Passed ✅",
          description: `All ${response.data.results.length} test steps completed successfully`,
        });
      } else {
        toast({
          title: "Credits E2E Test Failed ❌",
          description: `${response.data.results.filter((r: any) => r.status === 'FAIL').length} step(s) failed`,
          variant: "destructive"
        });
      }
    } catch (error: any) {
      toast({
        title: "Credits E2E Test Error",
        description: error.message,
        variant: "destructive"
      });
      
      setCreditsTestResults({
        status: 'FAIL',
        results: [{
          step: 'Test Execution',
          status: 'FAIL',
          details: error.message
        }]
      });
    } finally {
      setIsRunningCreditsTest(false);
    }
  };

  const sendTestSms = async () => {
    if (!smsPhone.trim() || !smsMessage.trim()) return;
    setIsSendingSms(true);
    try {
      const { data, error } = await supabase.functions.invoke('test-sms', {
        body: { phoneNumber: smsPhone.trim(), message: smsMessage.trim() }
      });
      if (error) throw error;
      if (data.success) {
        toast({ title: 'SMS sent', description: `Sent to ${data.to} from ${data.from}` });
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (error: any) {
      toast({ title: 'SMS failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsSendingSms(false);
    }
  };

  const addTestResult = (name: string, status: 'PASS' | 'FAIL' | 'PENDING', details?: string) => {
    setTestResults(prev => [...prev, { name, status, details }]);
  };

  const updateTestResult = (name: string, status: 'PASS' | 'FAIL' | 'PENDING', details?: string) => {
    setTestResults(prev => prev.map(result => 
      result.name === name ? { ...result, status, details } : result
    ));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PASS': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'FAIL': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variant = status === 'PASS' ? 'default' : status === 'FAIL' ? 'destructive' : 'secondary';
    return <Badge variant={variant}>{status}</Badge>;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">CallSparker QA Test Harness</h1>
        <div className="space-x-2">
          <Button onClick={runFullTest} disabled={isRunning} className="space-x-2">
            <Play className="h-4 w-4" />
            <span>{isRunning ? 'Running...' : 'Run Full Test'}</span>
          </Button>
          <Button 
            onClick={runCreditsE2ETest}
            disabled={isRunningCreditsTest}
            variant="secondary" 
            className="space-x-2"
          >
            <Play className="h-4 w-4" />
            <span>{isRunningCreditsTest ? 'Running Credits Test...' : 'Run Credits E2E Test'}</span>
          </Button>
          <Button onClick={cleanupTestData} variant="outline" className="space-x-2">
            <Trash2 className="h-4 w-4" />
            <span>Cleanup Test Data</span>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Test Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><strong>Webhook URL:</strong> {TEST_CONFIG.WEBHOOK_URL}</div>
            <div><strong>Test User:</strong> {TEST_CONFIG.TEST_USER}</div>
            <div><strong>App URL:</strong> {TEST_CONFIG.APP_URL}</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Test Scenarios</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {testData.campaign ? buildTestScenarios(testData.campaign.id).map((scenario, index) => (
              <div key={index} className="border rounded p-4">
                <h4 className="font-semibold">{scenario.name}</h4>
                <div className="text-sm text-muted-foreground mt-1">
                  Events: {scenario.events.length} | Assertions: {scenario.assertions.length}
                </div>
                <div className="mt-2">
                  <strong>Events:</strong> {scenario.events.map(e => e.data.toolName).join(', ')}
                </div>
              </div>
            )) : (
              <p className="text-muted-foreground">Run test to see scenarios</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Credits E2E Test Results */}
      {creditsTestResults && (
        <Card>
          <CardHeader>
            <CardTitle>Credits E2E Test Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Overall Status:</span>
                <div className="flex items-center space-x-2">
                  {creditsTestResults.status === 'PASS' ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <Badge variant={creditsTestResults.status === 'PASS' ? 'default' : 'destructive'}>
                    {creditsTestResults.status}
                  </Badge>
                </div>
              </div>
              
              {creditsTestResults.duration_ms && (
                <div className="flex items-center justify-between">
                  <span className="font-medium">Duration:</span>
                  <span className="text-sm">{creditsTestResults.duration_ms}ms</span>
                </div>
              )}

              <div className="space-y-2">
                <h4 className="font-medium">Test Steps:</h4>
                {creditsTestResults.results?.map((result: any, index: number) => (
                  <div key={index} className="border rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{result.step}</span>
                      <div className="flex items-center space-x-2">
                        {result.status === 'PASS' ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : result.status === 'FAIL' ? (
                          <XCircle className="h-4 w-4 text-red-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-yellow-500" />
                        )}
                        <Badge 
                          variant={result.status === 'PASS' ? 'default' : result.status === 'FAIL' ? 'destructive' : 'secondary'}
                          className="text-xs"
                        >
                          {result.status}
                        </Badge>
                      </div>
                    </div>
                    {result.details && (
                      <p className="text-xs text-muted-foreground mb-2">{result.details}</p>
                    )}
                    {result.expected && (
                      <div className="text-xs">
                        <div className="text-muted-foreground">Expected: {JSON.stringify(result.expected)}</div>
                        {result.actual && <div className="text-muted-foreground">Actual: {JSON.stringify(result.actual)}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Test Results</CardTitle>
        </CardHeader>
        <CardContent>
          {testResults.length === 0 ? (
            <p className="text-muted-foreground">No tests run yet. Click "Run Full Test" to begin.</p>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-4 font-semibold border-b pb-2">
                <div>Test Name</div>
                <div>Status</div>
                <div>Details</div>
                <div>Icon</div>
              </div>
              {testResults.map((result, index) => (
                <div key={index} className="grid grid-cols-4 gap-4 py-2 border-b">
                  <div className="text-sm">{result.name}</div>
                  <div>{getStatusBadge(result.status)}</div>
                  <div className="text-xs text-muted-foreground">{result.details || '—'}</div>
                  <div>{getStatusIcon(result.status)}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Manual Verification Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Navigate to <Link to="/actions" className="text-blue-500 underline">/actions</Link> to verify items appear</li>
            <li>Click "Mark Done" on an action to verify functionality</li>
            <li>Check that streaming media URLs are short-lived (no permanent storage)</li>
            <li>Verify that owner emails use plain-text template format</li>
            <li>Confirm missing prospect emails show as "—"</li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>SMS Delivery Test</CardTitle>
          <CardDescription>
            Verify Twilio SMS delivery when SMS is enabled. Requires Twilio credentials configured in API Settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sms-phone">Phone Number</Label>
              <Input
                id="sms-phone"
                type="tel"
                placeholder="+12145551234"
                value={smsPhone}
                onChange={(e) => setSmsPhone(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Include country code (e.g. +1 for US)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sms-message">Message</Label>
              <Textarea
                id="sms-message"
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">{smsMessage.length}/160 characters</p>
            </div>
          </div>
          <Button
            onClick={sendTestSms}
            disabled={isSendingSms || !smsPhone.trim() || !smsMessage.trim()}
            className="w-full"
          >
            {isSendingSms ? 'Sending...' : 'Send Test SMS'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default QATestHarness;